import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

interface CopyButtonProps {
  text: string;
}

// How long the check mark stays before the button returns to its copy icon.
const COPIED_RESET_MS = 2000;

const CopyButton: React.FC<CopyButtonProps> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleOnClick = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error("Failed to copy to clipboard", error);
      return;
    }
    setCopied(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
  };

  return (
    <Box>
      <Tooltip
        title={
          copied
            ? i18n._(
                msg({
                  context:
                    "Tooltip shown after the user copied a message to the clipboard.",
                  message: "Copied",
                }),
              )
            : i18n._(
                msg({
                  context:
                    "Title of a button which copies a message (the user's question or the application's answer) to the clipboard.",
                  message: "Copy",
                }),
              )
        }
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              fontSize: "0.9rem",
            },
          },
        }}
      >
        <span>
          <IconButton onClick={handleOnClick} size="small">
            {copied ? (
              <CheckIcon fontSize="small" />
            ) : (
              <ContentCopyIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default CopyButton;
